import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ReactQuill from 'react-quill-new';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';

// Register Custom Resizable Image Blot & Parchment Attributors so Quill NEVER strips 'style', 'width', 'height', or 'class'
const BaseImage = Quill.import('formats/image');
const Parchment = Quill.import('parchment');
const IMAGE_ATTRIBUTES = ['alt', 'height', 'width', 'style', 'class'];

if (Parchment?.Attributor) {
  const StyleAttr = new Parchment.Attributor('style', 'style', {
    scope: Parchment.Scope.INLINE_BLOT
  });
  const WidthAttr = new Parchment.Attributor('width', 'width', {
    scope: Parchment.Scope.INLINE_BLOT
  });
  const HeightAttr = new Parchment.Attributor('height', 'height', {
    scope: Parchment.Scope.INLINE_BLOT
  });
  Quill.register(StyleAttr, true);
  Quill.register(WidthAttr, true);
  Quill.register(HeightAttr, true);
  if (ReactQuill.Quill && ReactQuill.Quill !== Quill) {
    ReactQuill.Quill.register(StyleAttr, true);
    ReactQuill.Quill.register(WidthAttr, true);
    ReactQuill.Quill.register(HeightAttr, true);
  }
}

class ResizableImage extends BaseImage {
  static blotName = 'image';
  static tagName = 'IMG';

  static create(value) {
    let src = '';
    let attrs = {};
    if (typeof value === 'string') {
      src = value;
    } else if (typeof value === 'object' && value !== null) {
      src = value.src || value.url || '';
      attrs = value;
    }
    const node = super.create(src);
    IMAGE_ATTRIBUTES.forEach((attr) => {
      if (attrs[attr]) {
        node.setAttribute(attr, attrs[attr]);
      }
    });
    return node;
  }

  static formats(domNode) {
    return IMAGE_ATTRIBUTES.reduce((formats, attribute) => {
      if (domNode.hasAttribute(attribute)) {
        formats[attribute] = domNode.getAttribute(attribute);
      }
      return formats;
    }, {});
  }

  static value(domNode) {
    const src = domNode.getAttribute('src');
    const style = domNode.getAttribute('style');
    const width = domNode.getAttribute('width');
    const height = domNode.getAttribute('height');
    const alt = domNode.getAttribute('alt');
    const className = domNode.getAttribute('class');
    if (style || width || height || alt || className) {
      return {
        src,
        ...(style ? { style } : {}),
        ...(width ? { width } : {}),
        ...(height ? { height } : {}),
        ...(alt ? { alt } : {}),
        ...(className ? { class: className } : {})
      };
    }
    return src;
  }

  format(name, value) {
    if (IMAGE_ATTRIBUTES.indexOf(name) > -1) {
      if (value) {
        this.domNode.setAttribute(name, value);
      } else {
        this.domNode.removeAttribute(name);
      }
    } else {
      super.format(name, value);
    }
  }
}

Quill.register(ResizableImage, true);
if (ReactQuill.Quill && ReactQuill.Quill !== Quill) {
  ReactQuill.Quill.register(ResizableImage, true);
}
import {
  Box,
  Button,
  IconButton,
  Tooltip,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  TextField,
  CircularProgress,
  RadioGroup,
  FormControlLabel,
  Radio,
  Typography,
  Switch,
  Autocomplete
} from '@mui/material';
import {
  ArrowLeft,
  Save,
  Eye,
  Globe,
  Undo2,
  Redo2,
  Plus,
  Image as ImageIcon,
  LayoutGrid,
  Video,
  Settings,
  Sliders,
  X,
  Upload,
  Trash2,
  AlignLeft,
  AlignCenter,
  AlignRight
} from 'lucide-react';
import { contentAPI, adminAPI, trekAPI } from '../services/api';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import { buildBlogBentoGridHtml } from '../constants/blogBentoGridHtml';
import BlogPreviewModal from '../components/BlogPreviewModal';
import './BlogEditor.css';

const slugify = (value = '') =>
  String(value || '')
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');

function getEmbedUrl(rawUrl) {
  if (!rawUrl) return '';
  const url = rawUrl.trim();
  const ytMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  if (ytMatch) {
    return `https://www.youtube-nocookie.com/embed/${ytMatch[1]}`;
  }
  const vimeoMatch = url.match(/vimeo\.com\/(?:video\/)?([0-9]+)/);
  if (vimeoMatch) {
    return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
  }
  return url;
}

const CATEGORY_OPTIONS = [
  'Tips & Guides',
  'Trekking',
  'Night Sky',
  'Destinations',
  'Community',
  'Heritage',
  'Camp Experience',
  'Events',
  'Gear & Prep'
];

/**
 * Normalizes content into HTML with proper <p> and <br> tags.
 * Converts plain text / markdown newlines (\n\n) into paragraphs so text does
 * not collapse into a single giant paragraph block.
 */
function prepareContentForQuill(content) {
  if (!content) return '<p><br></p>';
  let str = String(content).trim();
  if (!str) return '<p><br></p>';

  // Check if string already contains HTML structural tags
  const hasHtml = /<\/?(p|div|h[1-6]|ul|ol|li|blockquote|table|img|iframe|video|figure|article|section)\b/i.test(str);

  if (!hasHtml) {
    // Plain text or markdown with \n\n paragraph breaks!
    // Split by double (or more) newlines:
    const paragraphs = str.split(/\r?\n\s*\r?\n+/);
    return paragraphs
      .map((p) => {
        const trimmedP = p.trim();
        if (!trimmedP) return '';
        // Convert single newlines inside paragraph to <br>
        const withBr = trimmedP.replace(/\r?\n/g, '<br>');
        return `<p>${withBr}</p>`;
      })
      .filter(Boolean)
      .join('');
  }

  // If HTML contains consecutive <br><br>, convert them to paragraph breaks
  str = str.replace(/(?:<br\s*\/?>\s*){2,}/gi, '</p><p>');

  return str;
}

/**
 * Loads content into Quill ensuring delta conversion preserves image attributes,
 * paragraph tags, and line breaks without stripping.
 */
function loadContentIntoQuill(editor, rawContent) {
  if (!editor) return;
  const htmlToLoad = prepareContentForQuill(rawContent);

  try {
    if (editor.clipboard && typeof editor.clipboard.convert === 'function') {
      const delta = editor.clipboard.convert({ html: htmlToLoad, text: '' });
      if (delta && delta.ops && delta.ops.length > 0) {
        editor.setContents(delta, 'silent');
        return;
      }
    }
  } catch {
    // fallback to innerHTML if convert fails
  }

  if (editor.root) {
    editor.root.innerHTML = htmlToLoad;
    try {
      editor.update?.('silent');
    } catch {
      // fallback
    }
  }
}

const BlogEditor = () => {
  const { id } = useParams();
  const isEditing = Boolean(id);
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { user } = useAuth();

  // Form State
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    category: '',
    author: user?.name || 'Invincible NGO',
    imageUrl: '',
    imageAlt: '',
    shortDescription: '',
    content: '',
    tags: [],
    relatedPostIds: [],
    relatedEventIds: [],
    isFeatured: false,
    isPublished: false,
    publishedAt: null,
    metaTitle: '',
    metaDescription: ''
  });

  const [isLoading, setIsLoading] = useState(isEditing);
  const [isSaving, setIsSaving] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  // Right Sidebar Active Tab: 'settings' | 'content' | null
  const [activeSidebarTab, setActiveSidebarTab] = useState('settings');

  // Related Options Data
  const [allBlogs, setAllBlogs] = useState([]);
  const [allEvents, setAllEvents] = useState([]);

  // Media Insertion Modal State
  const [mediaModalOpen, setMediaModalOpen] = useState(false);
  const [mediaTab, setMediaTab] = useState(0); // 0: Image, 1: Gallery, 2: Video
  const [mediaBusy, setMediaBusy] = useState(false);

  // Image Tab State
  const [imageMode, setImageMode] = useState('upload'); // 'upload' | 'url'
  const [imageFile, setImageFile] = useState(null);
  const [imageUrlInput, setImageUrlInput] = useState('');
  const [imageCaption, setImageCaption] = useState('');
  const [imageAlign, setImageAlign] = useState('center');

  // Gallery Tab State
  const [galleryCount, setGalleryCount] = useState(3); // 3 | 4 | 6
  const [galleryFiles, setGalleryFiles] = useState([]);

  // Video Tab State
  const [videoMode, setVideoMode] = useState('embed'); // 'embed' | 'upload'
  const [videoUrlInput, setVideoUrlInput] = useState('');
  const [videoFile, setVideoFile] = useState(null);

  // Quill Editor Reference & Title Textarea Reference
  const quillRef = useRef(null);
  const quillAreaRef = useRef(null);
  const titleTextareaRef = useRef(null);
  const initialLoadedRef = useRef(false);

  // Floating Line Plus Button State (Left of active line)
  const [plusTop, setPlusTop] = useState(6);
  const [isPlusVisible, setIsPlusVisible] = useState(true);
  const [isPlusMenuOpen, setIsPlusMenuOpen] = useState(false);
  const activeBlockRef = useRef(null);

  // Selected Image & Resizer State
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageBox, setImageBox] = useState(null);
  const [isResizing, setIsResizing] = useState(false);

  // Auto-resize title textarea to fit 2+ lines
  const adjustTitleHeight = () => {
    if (titleTextareaRef.current) {
      titleTextareaRef.current.style.height = 'auto';
      titleTextareaRef.current.style.height = `${titleTextareaRef.current.scrollHeight}px`;
    }
  };

  // Fetch blogs & events for Related select options
  useEffect(() => {
    const fetchRelatedOptions = async () => {
      try {
        const [blogsRes, treksRes] = await Promise.allSettled([
          contentAPI.getBlogs({ includeDrafts: true }),
          trekAPI.list()
        ]);

        if (blogsRes.status === 'fulfilled') {
          const list = Array.isArray(blogsRes.value?.data?.data)
            ? blogsRes.value.data.data
            : Array.isArray(blogsRes.value?.data)
            ? blogsRes.value.data
            : [];
          setAllBlogs(list);
        }

        if (treksRes.status === 'fulfilled') {
          const treks = Array.isArray(treksRes.value?.data?.data)
            ? treksRes.value.data.data
            : Array.isArray(treksRes.value?.data)
            ? treksRes.value.data
            : [];
          setAllEvents(treks);
        }
      } catch (e) {
        console.error('Error loading related options:', e);
      }
    };

    fetchRelatedOptions();
  }, []);

  // Fetch blog data if editing
  useEffect(() => {
    if (!isEditing) {
      initialLoadedRef.current = true;
      return;
    }

    let isMounted = true;
    const fetchBlog = async () => {
      setIsLoading(true);
      try {
        let blogData = null;
        try {
          const res = await contentAPI.getBlogById(id);
          blogData = res.data?.data || res.data;
        } catch {
          const listRes = await contentAPI.getBlogs({ includeDrafts: true });
          const list = Array.isArray(listRes.data?.data)
            ? listRes.data.data
            : Array.isArray(listRes.data)
            ? listRes.data
            : [];
          blogData = list.find((b) => String(b.id) === String(id));
        }

        if (isMounted && blogData) {
          setFormData({
            title: blogData.title || '',
            slug: blogData.slug || '',
            category: blogData.category || '',
            author: blogData.author || user?.name || 'Invincible NGO',
            imageUrl: blogData.imageUrl || '',
            imageAlt: blogData.imageAlt || '',
            shortDescription: blogData.shortDescription || '',
            content: blogData.content || '',
            tags: Array.isArray(blogData.tags) ? blogData.tags : [],
            relatedPostIds: Array.isArray(blogData.relatedPostIds) ? blogData.relatedPostIds : [],
            relatedEventIds: Array.isArray(blogData.relatedEventIds) ? blogData.relatedEventIds : [],
            isFeatured: Boolean(blogData.isFeatured),
            isPublished: Boolean(blogData.isPublished),
            publishedAt: blogData.publishedAt || null,
            metaTitle: blogData.metaTitle || '',
            metaDescription: blogData.metaDescription || ''
          });

          // Sync into Quill editor
          if (quillRef.current && blogData.content) {
            const editor = quillRef.current.getEditor();
            loadContentIntoQuill(editor, blogData.content);
          }

          // Adjust title height after state loads
          setTimeout(adjustTitleHeight, 50);
        }
      } catch (err) {
        showToast?.(err.message || 'Error loading blog details.', 'error');
      } finally {
        if (isMounted) {
          setIsLoading(false);
          initialLoadedRef.current = true;
        }
      }
    };

    fetchBlog();
    return () => {
      isMounted = false;
    };
  }, [id, isEditing, showToast, user?.name]);

  // Sync content into Quill once editor is mounted or content is loaded
  useEffect(() => {
    if (!isLoading && quillRef.current && formData.content) {
      const editor = quillRef.current.getEditor ? quillRef.current.getEditor() : null;
      if (editor) {
        const text = editor.getText().trim();
        if (!text) {
          loadContentIntoQuill(editor, formData.content);
        }
      }
    }
  }, [isLoading, formData.content]);

  // Reposition floating plus button aligned to active line/block
  const updatePlusPosition = useCallback(() => {
    if (!quillRef.current || !quillAreaRef.current) return;
    const editor = quillRef.current.getEditor ? quillRef.current.getEditor() : null;
    if (!editor || !editor.root) return;

    const areaRect = quillAreaRef.current.getBoundingClientRect();
    const selection = window.getSelection();

    let targetBlock = null;
    if (selection && selection.rangeCount > 0) {
      let node = selection.getRangeAt(0).startContainer;
      if (node.nodeType === Node.TEXT_NODE) node = node.parentNode;
      targetBlock = node?.closest?.('.ql-editor > *');
    }

    if (!targetBlock) {
      targetBlock = editor.root.firstElementChild;
    }

    if (targetBlock) {
      activeBlockRef.current = targetBlock;
      const blockRect = targetBlock.getBoundingClientRect();
      const relativeTop = blockRect.top - areaRect.top;
      // Vertically center with the text line
      const topOffset = Math.max(0, relativeTop + 2);
      setPlusTop(topOffset);
      setIsPlusVisible(true);
    }
  }, []);

  // Track active line via Quill events
  useEffect(() => {
    if (isLoading) return;
    const timer = setTimeout(() => {
      updatePlusPosition();
      const editor = quillRef.current?.getEditor?.();
      if (!editor || !editor.root) return;

      const handleSelection = () => updatePlusPosition();
      const handleKeyUp = () => updatePlusPosition();
      const handleMouseOver = (e) => {
        const block = e.target.closest?.('.ql-editor > *');
        if (block && quillAreaRef.current) {
          activeBlockRef.current = block;
          const areaRect = quillAreaRef.current.getBoundingClientRect();
          const blockRect = block.getBoundingClientRect();
          const relativeTop = blockRect.top - areaRect.top;
          setPlusTop(Math.max(0, relativeTop + 2));
          setIsPlusVisible(true);
        }
      };

      const handleEditorRootClick = (e) => {
        handleSelection();
        const img = e.target.closest?.('img');
        if (img && editor.root.contains(img)) {
          e.stopPropagation();
          setSelectedImage(img);
          updateSelectedImageBox(img);
        }
      };

      editor.on('selection-change', handleSelection);
      editor.root.addEventListener('keyup', handleKeyUp);
      editor.root.addEventListener('mouseover', handleMouseOver);
      editor.root.addEventListener('click', handleEditorRootClick);

      return () => {
        editor.off('selection-change', handleSelection);
        editor.root.removeEventListener('keyup', handleKeyUp);
        editor.root.removeEventListener('mouseover', handleMouseOver);
        editor.root.removeEventListener('click', handleEditorRootClick);
      };
    }, 150);

    return () => clearTimeout(timer);
  }, [isLoading, updatePlusPosition]);

  // Update image bounding box relative to canvas quill area
  const updateSelectedImageBox = useCallback((imgEl) => {
    if (!imgEl || !quillAreaRef.current) {
      setImageBox(null);
      return;
    }
    const areaRect = quillAreaRef.current.getBoundingClientRect();
    const imgRect = imgEl.getBoundingClientRect();
    setImageBox({
      top: imgRect.top - areaRect.top,
      left: imgRect.left - areaRect.left,
      width: imgRect.width,
      height: imgRect.height
    });
  }, []);

  // Handle clicking outside selected image, escape, and window resize/scroll
  useEffect(() => {
    const handleDocumentClick = (e) => {
      if (
        !e.target.closest('.blog-image-resizer-overlay') &&
        !e.target.closest('img')
      ) {
        setSelectedImage(null);
        setImageBox(null);
      }
    };

    const handleWindowResizeOrScroll = () => {
      if (selectedImage) {
        updateSelectedImageBox(selectedImage);
      }
    };

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setSelectedImage(null);
        setImageBox(null);
      } else if ((e.key === 'Delete' || e.key === 'Backspace') && selectedImage && !isResizing) {
        const tag = document.activeElement?.tagName?.toLowerCase();
        if (tag !== 'input' && tag !== 'textarea') {
          handleDeleteImage();
        }
      }
    };

    document.addEventListener('click', handleDocumentClick);
    window.addEventListener('scroll', handleWindowResizeOrScroll, true);
    window.addEventListener('resize', handleWindowResizeOrScroll);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('click', handleDocumentClick);
      window.removeEventListener('scroll', handleWindowResizeOrScroll, true);
      window.removeEventListener('resize', handleWindowResizeOrScroll);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedImage, isResizing, updateSelectedImageBox]);

  // Drag-to-resize handler for corners and edges
  const handleResizeStart = (e, direction) => {
    e.preventDefault();
    e.stopPropagation();
    if (!selectedImage || !quillAreaRef.current) return;

    setIsResizing(true);
    const startX = e.clientX;
    const startWidth = selectedImage.getBoundingClientRect().width;
    const startHeight = selectedImage.getBoundingClientRect().height;
    const aspectRatio = startWidth / (startHeight || 1);
    const containerWidth = quillAreaRef.current.offsetWidth || 800;

    const onPointerMove = (moveEvent) => {
      moveEvent.preventDefault();
      const deltaX = moveEvent.clientX - startX;
      let newWidth = startWidth;

      if (direction.includes('e')) {
        newWidth = startWidth + deltaX;
      } else if (direction.includes('w')) {
        newWidth = startWidth - deltaX;
      } else if (direction === 's') {
        const deltaY = moveEvent.clientY - e.clientY;
        newWidth = (startHeight + deltaY) * aspectRatio;
      } else if (direction === 'n') {
        const deltaY = moveEvent.clientY - e.clientY;
        newWidth = (startHeight - deltaY) * aspectRatio;
      }

      // Constrain width
      newWidth = Math.max(90, Math.min(newWidth, containerWidth));
      const newHeight = Math.round(newWidth / aspectRatio);

      selectedImage.style.width = `${Math.round(newWidth)}px`;
      selectedImage.style.height = 'auto';
      selectedImage.style.maxWidth = '100%';
      selectedImage.setAttribute('width', Math.round(newWidth));
      selectedImage.setAttribute('height', Math.round(newHeight));

      updateSelectedImageBox(selectedImage);
      setHasUnsavedChanges(true);
    };

    const onPointerUp = () => {
      setIsResizing(false);
      document.removeEventListener('pointermove', onPointerMove);
      document.removeEventListener('pointerup', onPointerUp);
      try {
        const blot = Quill.find ? Quill.find(selectedImage) : null;
        if (blot && blot.format) {
          blot.format('style', selectedImage.getAttribute('style'));
          blot.format('width', selectedImage.getAttribute('width'));
          blot.format('height', selectedImage.getAttribute('height'));
        }
      } catch {
        // fallback
      }
      if (quillRef.current) {
        const editor = quillRef.current.getEditor();
        editor?.update?.('user');
      }
    };

    document.addEventListener('pointermove', onPointerMove);
    document.addEventListener('pointerup', onPointerUp);
  };

  // Align image left, center, or right
  const handleAlignImage = (align) => {
    if (!selectedImage) return;
    const figure = selectedImage.closest('figure');
    if (align === 'left') {
      selectedImage.style.float = 'left';
      selectedImage.style.margin = '0.5rem 1.5rem 1rem 0';
      selectedImage.style.display = 'block';
      if (figure) {
        figure.className = 'blog-media-figure blog-img-align-left';
        figure.style.float = 'left';
        figure.style.margin = '0.5rem 1.5rem 1rem 0';
      }
    } else if (align === 'right') {
      selectedImage.style.float = 'right';
      selectedImage.style.margin = '0.5rem 0 1rem 1.5rem';
      selectedImage.style.display = 'block';
      if (figure) {
        figure.className = 'blog-media-figure blog-img-align-right';
        figure.style.float = 'right';
        figure.style.margin = '0.5rem 0 1rem 1.5rem';
      }
    } else if (align === 'center') {
      selectedImage.style.float = 'none';
      selectedImage.style.marginLeft = 'auto';
      selectedImage.style.marginRight = 'auto';
      selectedImage.style.display = 'block';
      if (figure) {
        figure.className = 'blog-media-figure blog-img-align-center';
        figure.style.float = 'none';
        figure.style.marginLeft = 'auto';
        figure.style.marginRight = 'auto';
        figure.style.textAlign = 'center';
      }
    }
    try {
      const blot = Quill.find ? Quill.find(selectedImage) : null;
      if (blot && blot.format) {
        blot.format('style', selectedImage.getAttribute('style'));
      }
    } catch {
      // fallback
    }
    updateSelectedImageBox(selectedImage);
    setHasUnsavedChanges(true);
    if (quillRef.current) {
      const editor = quillRef.current.getEditor();
      editor?.update?.('user');
    }
  };

  // Set preset width percentage (25%, 50%, 75%, 100%)
  const handleSetImagePercent = (pct) => {
    if (!selectedImage) return;
    const styleVal = `width: ${pct}%; height: auto; max-width: 100%; display: block; margin: 0.5rem auto 1rem;`;
    selectedImage.setAttribute('style', styleVal);
    selectedImage.setAttribute('width', `${pct}%`);
    try {
      const blot = Quill.find ? Quill.find(selectedImage) : null;
      if (blot && blot.format) {
        blot.format('style', styleVal);
        blot.format('width', `${pct}%`);
      }
    } catch {
      // fallback
    }
    updateSelectedImageBox(selectedImage);
    setHasUnsavedChanges(true);
    if (quillRef.current) {
      const editor = quillRef.current.getEditor();
      editor?.update?.('user');
    }
  };

  // Delete currently selected image
  const handleDeleteImage = () => {
    if (!selectedImage) return;
    const figure = selectedImage.closest('figure');
    if (figure) {
      figure.remove();
    } else {
      selectedImage.remove();
    }
    setSelectedImage(null);
    setImageBox(null);
    setHasUnsavedChanges(true);
    if (quillRef.current) {
      const editor = quillRef.current.getEditor();
      editor?.update?.('user');
    }
  };

  // Close floating plus popup when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.blog-floating-line-plus-wrapper')) {
        setIsPlusMenuOpen(false);
      }
    };
    if (isPlusMenuOpen) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [isPlusMenuOpen]);

  // Handler to open media modal directly from line + button
  const handleOpenMediaFromLine = (tabIndex) => {
    setIsPlusMenuOpen(false);
    setMediaTab(tabIndex);
    setMediaModalOpen(true);
  };

  // Handle Title Change (allows multiple lines naturally)
  const handleTitleChange = (e) => {
    const newTitle = e.target.value;
    setHasUnsavedChanges(true);
    adjustTitleHeight();
    setFormData((prev) => {
      const currentSlug = prev.slug;
      const shouldAutoSlug = !isEditing || !currentSlug || currentSlug === slugify(prev.title);
      return {
        ...prev,
        title: newTitle,
        slug: shouldAutoSlug ? slugify(newTitle) : currentSlug
      };
    });
  };

  // Undo / Redo Handlers
  const handleUndo = () => {
    const editor = quillRef.current?.getEditor();
    if (editor?.history) {
      editor.history.undo();
    }
  };

  const handleRedo = () => {
    const editor = quillRef.current?.getEditor();
    if (editor?.history) {
      editor.history.redo();
    }
  };

  // Insert HTML into Editor at current selection
  const insertHtmlAtCursor = useCallback((html) => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;
    const range = editor.getSelection(true) || { index: editor.getLength() };
    editor.clipboard.dangerouslyPasteHTML(range.index, html, 'user');
    editor.setSelection(range.index + 1);
    setHasUnsavedChanges(true);
  }, []);

  // Cover image file upload handler for Sidebar
  const handleCoverUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const res = await adminAPI.uploadImage(file);
      const url = res.data?.data?.url || res.data?.url;
      setFormData((prev) => ({ ...prev, imageUrl: url }));
      setHasUnsavedChanges(true);
      showToast?.('Cover image uploaded!', 'success');
    } catch {
      showToast?.('Failed to upload cover image.', 'error');
    }
  };

  // Word count helper for Excerpt (90 words max)
  const excerptWordCount = formData.shortDescription?.trim()
    ? formData.shortDescription.trim().split(/\s+/).filter(Boolean).length
    : 0;

  // Save Blog (Draft / Save changes without publishing)
  const handleSave = async (publishFlag = null) => {
    const editor = quillRef.current?.getEditor();
    const currentHtml = editor ? editor.root.innerHTML : formData.content;

    if (!formData.title?.trim()) {
      showToast?.('Please enter a blog title.', 'warning');
      return;
    }

    const targetIsPublished = publishFlag !== null ? publishFlag : formData.isPublished;
    const payload = {
      ...formData,
      content: currentHtml,
      slug: formData.slug?.trim() ? slugify(formData.slug) : slugify(formData.title),
      isPublished: targetIsPublished,
      publishedAt:
        targetIsPublished && !formData.publishedAt
          ? new Date().toISOString()
          : formData.publishedAt
    };

    if (publishFlag === true) {
      setIsPublishing(true);
    } else {
      setIsSaving(true);
    }

    try {
      if (isEditing) {
        const res = await contentAPI.updateBlog(id, payload);
        const updated = res.data?.data || res.data;
        setFormData((prev) => ({
          ...prev,
          ...updated,
          isPublished: targetIsPublished
        }));
        showToast?.(
          publishFlag === true
            ? '🎉 Blog published live!'
            : 'Blog changes saved successfully!',
          'success'
        );
      } else {
        const res = await contentAPI.createBlog(payload);
        const created = res.data?.data || res.data;
        showToast?.(
          publishFlag === true
            ? '🎉 Blog published live!'
            : 'Draft saved successfully!',
          'success'
        );
        if (created?.id) {
          navigate(`/blogs/edit/${created.id}`, { replace: true });
        }
      }
      setHasUnsavedChanges(false);
    } catch (err) {
      showToast?.(err.response?.data?.message || err.message || 'Error saving blog.', 'error');
    } finally {
      setIsSaving(false);
      setIsPublishing(false);
    }
  };

  // Media Insertion Handler (Modal)
  const handleInsertMedia = async () => {
    setMediaBusy(true);
    try {
      if (mediaTab === 0) {
        // --- 1. SINGLE IMAGE ---
        let src = imageUrlInput.trim();
        if (imageMode === 'upload') {
          if (!imageFile) {
            showToast?.('Please select an image file to upload.', 'warning');
            setMediaBusy(false);
            return;
          }
          const res = await adminAPI.uploadImage(imageFile);
          src = res.data?.data?.url || res.data?.url;
        }

        if (!src) {
          showToast?.('Image URL is missing.', 'error');
          setMediaBusy(false);
          return;
        }

        const alignCls =
          imageAlign === 'wide'
            ? 'blog-img-align-wide'
            : imageAlign === 'left'
            ? 'blog-img-align-left'
            : imageAlign === 'right'
            ? 'blog-img-align-right'
            : 'blog-img-align-center';

        const captionHtml = imageCaption.trim()
          ? `<figcaption style="text-align:center;font-size:0.875rem;color:#64748b;margin-top:0.4rem;">${imageCaption.trim()}</figcaption>`
          : '';

        const imageHtml = `
          <p><br></p>
          <figure class="blog-media-figure ${alignCls}" style="margin: 1.5rem 0; text-align: center;">
            <img src="${src}" alt="${imageCaption || 'Blog image'}" style="max-width: 100%; border-radius: 12px; height: auto;" />
            ${captionHtml}
          </figure>
          <p><br></p>
        `;

        insertHtmlAtCursor(imageHtml);
        showToast?.('Image inserted into blog!', 'success');
      } else if (mediaTab === 1) {
        // --- 2. GALLERY GRID ---
        const n = Number(galleryCount) || 3;
        if (galleryFiles.length < n) {
          showToast?.(`Please choose ${n} image files for this gallery grid.`, 'warning');
          setMediaBusy(false);
          return;
        }

        const urls = [];
        for (const file of galleryFiles.slice(0, n)) {
          const res = await adminAPI.uploadImage(file);
          const url = res.data?.data?.url || res.data?.url;
          if (!url) throw new Error('One of the image uploads failed.');
          urls.push(String(url).trim());
        }

        const gridHtml = buildBlogBentoGridHtml(n, urls);
        insertHtmlAtCursor(`<p><br></p>${gridHtml}<p><br></p>`);
        showToast?.(`${n}-photo gallery inserted!`, 'success');
      } else if (mediaTab === 2) {
        // --- 3. VIDEO ---
        if (videoMode === 'embed') {
          if (!videoUrlInput.trim()) {
            showToast?.('Please enter a YouTube, Vimeo, or video URL.', 'warning');
            setMediaBusy(false);
            return;
          }
          const embedUrl = getEmbedUrl(videoUrlInput);
          const isDirectVideo = embedUrl.endsWith('.mp4') || embedUrl.endsWith('.webm');

          const videoHtml = isDirectVideo
            ? `<div class="blog-video-container"><video controls class="blog-embedded-video" src="${embedUrl}"></video></div><p><br></p>`
            : `<div class="blog-video-container"><iframe src="${embedUrl}" title="Embedded video" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe></div><p><br></p>`;

          insertHtmlAtCursor(videoHtml);
          showToast?.('Video embed inserted!', 'success');
        } else {
          if (!videoFile) {
            showToast?.('Please choose a video file.', 'warning');
            setMediaBusy(false);
            return;
          }
          const res = await adminAPI.uploadTipsAsset(videoFile);
          const videoUrl = res.data?.data?.url || res.data?.url;
          if (!videoUrl) throw new Error('Video upload failed.');

          const videoHtml = `
            <div class="blog-video-container">
              <video controls class="blog-embedded-video" src="${videoUrl}"></video>
            </div>
            <p><br></p>
          `;
          insertHtmlAtCursor(videoHtml);
          showToast?.('Uploaded video inserted!', 'success');
        }
      }

      setMediaModalOpen(false);
      setImageFile(null);
      setImageUrlInput('');
      setImageCaption('');
      setGalleryFiles([]);
      setVideoUrlInput('');
      setVideoFile(null);
    } catch (err) {
      showToast?.(err.message || 'Error adding media.', 'error');
    } finally {
      setMediaBusy(false);
    }
  };

  // Quill Modules config linked to the custom toolbar
  const quillModules = {
    toolbar: '#blog-quill-custom-toolbar',
    history: {
      delay: 500,
      maxStack: 100,
      userOnly: true
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', gap: 2 }}>
        <CircularProgress size={32} sx={{ color: '#ff5500' }} />
        <Typography variant="body2" color="text.secondary">Loading blog editor…</Typography>
      </Box>
    );
  }

  return (
    <div className="blog-editor-page">
      {/* ------------------------------------------------------------------
          1. TOP ACTION BAR
          Back, Undo, Redo, Save, Preview, Publish
          ------------------------------------------------------------------ */}
      <div className="blog-editor-top-bar">
        {/* Left Side: Back & History */}
        <div className="blog-editor-top-left">
          <Button
            variant="text"
            startIcon={<ArrowLeft size={18} />}
            onClick={() => navigate('/content?tab=blogs')}
            sx={{
              color: '#334155',
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: 2,
              '&:hover': { bgcolor: '#f1f5f9' }
            }}
          >
            All Blogs
          </Button>

          <div className="blog-editor-divider" />

          {/* Undo & Redo */}
          <div className="blog-editor-history-group">
            <Tooltip title="Undo (Ctrl+Z)">
              <IconButton size="small" onClick={handleUndo} sx={{ p: 0.75 }}>
                <Undo2 size={16} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Redo (Ctrl+Y)">
              <IconButton size="small" onClick={handleRedo} sx={{ p: 0.75 }}>
                <Redo2 size={16} />
              </IconButton>
            </Tooltip>
          </div>

          <div className="blog-editor-divider" />

          {/* Status Badge & Indicator */}
          <Chip
            label={formData.isPublished ? 'Published' : 'Draft'}
            size="small"
            color={formData.isPublished ? 'success' : 'default'}
            sx={{ fontWeight: 700, fontSize: '0.75rem' }}
          />

          <div className="blog-editor-save-status">
            <span className={`blog-editor-save-dot ${hasUnsavedChanges ? 'unsaved' : ''}`} />
            <span>{hasUnsavedChanges ? 'Unsaved changes' : 'All changes saved'}</span>
          </div>
        </div>

        {/* Right Side: Save, Preview, Publish */}
        <div className="blog-editor-top-right">
          {/* Save Button (Saves without publishing) */}
          <Button
            variant="outlined"
            startIcon={isSaving ? <CircularProgress size={16} /> : <Save size={16} />}
            onClick={() => handleSave(null)}
            disabled={isSaving || isPublishing}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2,
              borderColor: '#cbd5e1',
              color: '#334155',
              '&:hover': { borderColor: '#94a3b8', bgcolor: '#f8fafc' }
            }}
          >
            {isSaving ? 'Saving…' : 'Save'}
          </Button>

          {/* Preview Button */}
          <Button
            variant="outlined"
            startIcon={<Eye size={16} />}
            onClick={() => {
              const editor = quillRef.current?.getEditor();
              const liveContent = editor ? editor.root.innerHTML : formData.content;
              setFormData((prev) => ({ ...prev, content: liveContent }));
              setPreviewOpen(true);
            }}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2,
              borderColor: '#cbd5e1',
              color: '#334155',
              '&:hover': { borderColor: '#94a3b8', bgcolor: '#f8fafc' }
            }}
          >
            Preview
          </Button>

          {/* Publish Button */}
          <Button
            variant="contained"
            startIcon={isPublishing ? <CircularProgress size={16} color="inherit" /> : <Globe size={16} />}
            onClick={() => handleSave(true)}
            disabled={isSaving || isPublishing}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: 2,
              bgcolor: '#ff5500',
              color: '#ffffff',
              boxShadow: '0 2px 8px rgba(255,85,0,0.25)',
              '&:hover': { bgcolor: '#e04b00' }
            }}
          >
            {isPublishing ? 'Publishing…' : formData.isPublished ? 'Update Live' : 'Publish'}
          </Button>
        </div>
      </div>

      {/* ------------------------------------------------------------------
          2. TEXT EDITS FORMATTING TOOLBAR
          Centered in the middle, uniform spacing, no Content AI, no Add Media
          ------------------------------------------------------------------ */}
      <div className="blog-editor-toolbar-wrap">
        <div id="blog-quill-custom-toolbar">
          {/* Header selector (Paragraph, Heading 1, 2, 3) */}
          <select className="ql-header" defaultValue="" aria-label="Text Style">
            <option value="">Paragraph</option>
            <option value="1">Heading 1</option>
            <option value="2">Heading 2</option>
            <option value="3">Heading 3</option>
          </select>

          {/* Font Size selector */}
          <select className="ql-size" defaultValue="normal" aria-label="Font Size">
            <option value="small">12</option>
            <option value="normal">15</option>
            <option value="large">18</option>
            <option value="huge">24</option>
          </select>

          <span className="blog-toolbar-v-divider" />

          {/* Basic Formatting */}
          <span className="ql-formats">
            <button className="ql-bold" title="Bold (Ctrl+B)" type="button" />
            <button className="ql-italic" title="Italic (Ctrl+I)" type="button" />
            <button className="ql-underline" title="Underline (Ctrl+U)" type="button" />
            <button className="ql-strike" title="Strikethrough" type="button" />
          </span>

          <span className="blog-toolbar-v-divider" />

          {/* Color & Background */}
          <span className="ql-formats">
            <select className="ql-color" title="Text Color" />
            <select className="ql-background" title="Highlight Color" />
          </span>

          <span className="blog-toolbar-v-divider" />

          {/* Link, Quote, Code */}
          <span className="ql-formats">
            <button className="ql-link" title="Insert Link" type="button" />
            <button className="ql-blockquote" title="Quote" type="button" />
            <button className="ql-code-block" title="Code Block" type="button" />
          </span>

          <span className="blog-toolbar-v-divider" />

          {/* Lists */}
          <span className="ql-formats">
            <button className="ql-list" value="bullet" title="Bullet List" type="button" />
            <button className="ql-list" value="ordered" title="Numbered List" type="button" />
          </span>

          <span className="blog-toolbar-v-divider" />

          {/* Alignment */}
          <span className="ql-formats">
            <select className="ql-align" title="Text Alignment" />
          </span>

          {/* Indent / Outdent */}
          <span className="ql-formats">
            <button className="ql-indent" value="-1" title="Outdent" type="button" />
            <button className="ql-indent" value="+1" title="Indent" type="button" />
          </span>

          <span className="blog-toolbar-v-divider" />

          {/* Clean */}
          <span className="ql-formats">
            <button className="ql-clean" title="Clear Formatting" type="button" />
          </span>
        </div>
      </div>

      {/* ------------------------------------------------------------------
          3. MAIN LAYOUT: Full Width Edit Box + Right Sidebar
          ------------------------------------------------------------------ */}
      <div className="blog-editor-main-layout">
        {/* Full-width document canvas column */}
        <div className="blog-editor-canvas-column">
          <div className="blog-canvas-sheet full-width">
            {/* FIRST LINE: Blog Title (Multiline auto-resizing textarea) */}
            <textarea
              ref={titleTextareaRef}
              rows={1}
              className="blog-canvas-title-textarea"
              placeholder="Title of your blog..."
              value={formData.title}
              onChange={handleTitleChange}
              autoFocus={!isEditing}
            />

            {/* Sub-bar below Title: Slug Preview */}
            <div className="blog-canvas-subbar">
              {formData.slug ? (
                <div className="blog-slug-preview-pill">
                  /blogs/{formData.slug}
                </div>
              ) : (
                <div />
              )}
              {formData.category && (
                <Chip
                  label={formData.category}
                  size="small"
                  variant="outlined"
                  sx={{ fontWeight: 600, fontSize: '0.75rem', borderColor: '#e2e8f0' }}
                />
              )}
            </div>

            {/* MAIN CONTENT QUILL AREA with Floating Line Plus Button */}
            <div className="blog-canvas-quill-area" ref={quillAreaRef}>
              {/* Floating + button on the left of any line */}
              <div
                className="blog-floating-line-plus-wrapper"
                style={{
                  top: `${plusTop}px`,
                  opacity: isPlusVisible ? 1 : 0
                }}
              >
                <button
                  type="button"
                  className={`blog-floating-line-plus-btn ${isPlusMenuOpen ? 'open' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsPlusMenuOpen((prev) => !prev);
                  }}
                  title="Add rich media (Image, Gallery, Video)"
                >
                  <Plus size={16} />
                </button>

                {isPlusMenuOpen && (
                  <div className="blog-floating-line-plus-menu">
                    <button
                      type="button"
                      className="blog-floating-menu-item"
                      onClick={() => handleOpenMediaFromLine(0)}
                    >
                      <span className="blog-floating-menu-icon image">
                        <ImageIcon size={15} />
                      </span>
                      <div className="blog-floating-menu-text">
                        <strong>Image</strong>
                        <small>Upload or image URL</small>
                      </div>
                    </button>

                    <button
                      type="button"
                      className="blog-floating-menu-item"
                      onClick={() => handleOpenMediaFromLine(1)}
                    >
                      <span className="blog-floating-menu-icon gallery">
                        <LayoutGrid size={15} />
                      </span>
                      <div className="blog-floating-menu-text">
                        <strong>Gallery Grid</strong>
                        <small>Bento photo collage</small>
                      </div>
                    </button>

                    <button
                      type="button"
                      className="blog-floating-menu-item"
                      onClick={() => handleOpenMediaFromLine(2)}
                    >
                      <span className="blog-floating-menu-icon video">
                        <Video size={15} />
                      </span>
                      <div className="blog-floating-menu-text">
                        <strong>Video</strong>
                        <small>YouTube or video upload</small>
                      </div>
                    </button>
                  </div>
                )}
              </div>

              <ReactQuill
                ref={quillRef}
                theme="snow"
                modules={quillModules}
                defaultValue={prepareContentForQuill(formData.content)}
                onChange={(_content, _delta, source) => {
                  if (source === 'user') {
                    setHasUnsavedChanges(true);
                  }
                }}
                placeholder="Start writing or type / for plugins"
              />

              {/* Interactive Image Resizer Overlay on Click */}
              {selectedImage && imageBox && (
                <div
                  className="blog-image-resizer-overlay"
                  style={{
                    top: `${imageBox.top}px`,
                    left: `${imageBox.left}px`,
                    width: `${imageBox.width}px`,
                    height: `${imageBox.height}px`
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {/* Corner Resize Handles */}
                  <div
                    className="blog-image-resize-handle handle-nw"
                    onPointerDown={(e) => handleResizeStart(e, 'nw')}
                    title="Drag corner to resize"
                  />
                  <div
                    className="blog-image-resize-handle handle-ne"
                    onPointerDown={(e) => handleResizeStart(e, 'ne')}
                    title="Drag corner to resize"
                  />
                  <div
                    className="blog-image-resize-handle handle-sw"
                    onPointerDown={(e) => handleResizeStart(e, 'sw')}
                    title="Drag corner to resize"
                  />
                  <div
                    className="blog-image-resize-handle handle-se"
                    onPointerDown={(e) => handleResizeStart(e, 'se')}
                    title="Drag corner to resize"
                  />

                  {/* Edge Resize Handles */}
                  <div
                    className="blog-image-resize-handle handle-w"
                    onPointerDown={(e) => handleResizeStart(e, 'w')}
                    title="Drag edge to change width"
                  />
                  <div
                    className="blog-image-resize-handle handle-e"
                    onPointerDown={(e) => handleResizeStart(e, 'e')}
                    title="Drag edge to change width"
                  />

                  {/* Live Dimensions Tooltip */}
                  <div className="blog-image-dimension-badge">
                    {Math.round(imageBox.width)} × {Math.round(imageBox.height)} px
                  </div>

                  {/* Floating Action Controls */}
                  <div className="blog-image-floating-toolbar" onClick={(e) => e.stopPropagation()}>
                    <button
                      type="button"
                      className="blog-img-tb-btn"
                      onClick={() => handleAlignImage('left')}
                      title="Float Left"
                    >
                      <AlignLeft size={13} />
                    </button>
                    <button
                      type="button"
                      className="blog-img-tb-btn"
                      onClick={() => handleAlignImage('center')}
                      title="Center"
                    >
                      <AlignCenter size={13} />
                    </button>
                    <button
                      type="button"
                      className="blog-img-tb-btn"
                      onClick={() => handleAlignImage('right')}
                      title="Float Right"
                    >
                      <AlignRight size={13} />
                    </button>

                    <div className="blog-img-tb-divider" />

                    <button
                      type="button"
                      className="blog-img-tb-btn"
                      onClick={() => handleSetImagePercent(25)}
                      title="Set 25% width"
                    >
                      25%
                    </button>
                    <button
                      type="button"
                      className="blog-img-tb-btn"
                      onClick={() => handleSetImagePercent(50)}
                      title="Set 50% width"
                    >
                      50%
                    </button>
                    <button
                      type="button"
                      className="blog-img-tb-btn"
                      onClick={() => handleSetImagePercent(75)}
                      title="Set 75% width"
                    >
                      75%
                    </button>
                    <button
                      type="button"
                      className="blog-img-tb-btn"
                      onClick={() => handleSetImagePercent(100)}
                      title="Set 100% width"
                    >
                      100%
                    </button>

                    <div className="blog-img-tb-divider" />

                    <button
                      type="button"
                      className="blog-img-tb-btn"
                      onClick={handleDeleteImage}
                      title="Delete image"
                      style={{ color: '#ef4444' }}
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ----------------------------------------------------------------
            RIGHT SIDEBAR: Icons of Settings and Content
            ---------------------------------------------------------------- */}
        <aside className="blog-editor-sidebar">
          {/* Vertical Icon Strip */}
          <div className="blog-sidebar-icon-strip">
            <Tooltip title="Post Settings (Cover, Excerpt, Category, Tags)" placement="left">
              <button
                type="button"
                className={`blog-sidebar-tab-btn ${activeSidebarTab === 'settings' ? 'active' : ''}`}
                onClick={() => setActiveSidebarTab(activeSidebarTab === 'settings' ? null : 'settings')}
              >
                <Settings size={20} />
              </button>
            </Tooltip>

            <Tooltip title="SEO & URL Slug (Search preview, meta tags)" placement="left">
              <button
                type="button"
                className={`blog-sidebar-tab-btn ${activeSidebarTab === 'seo' ? 'active' : ''}`}
                onClick={() => setActiveSidebarTab(activeSidebarTab === 'seo' ? null : 'seo')}
              >
                <Globe size={20} />
              </button>
            </Tooltip>

            <Tooltip title="Related Content & Featured Post" placement="left">
              <button
                type="button"
                className={`blog-sidebar-tab-btn ${activeSidebarTab === 'content' ? 'active' : ''}`}
                onClick={() => setActiveSidebarTab(activeSidebarTab === 'content' ? null : 'content')}
              >
                <Sliders size={20} />
              </button>
            </Tooltip>
          </div>

          {/* Active Sidebar Drawer Panel */}
          {activeSidebarTab && (
            <div className="blog-sidebar-panel">
              <div className="blog-sidebar-panel-header">
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0f172a' }}>
                  {activeSidebarTab === 'settings' && 'Post Settings'}
                  {activeSidebarTab === 'seo' && 'SEO & URL Slug'}
                  {activeSidebarTab === 'content' && 'Content & Relationships'}
                </Typography>
                <IconButton size="small" onClick={() => setActiveSidebarTab(null)}>
                  <X size={16} />
                </IconButton>
              </div>

              <div className="blog-sidebar-panel-body">
                {/* ============================================================
                    PANEL 1: SETTINGS
                    Cover image, Alt text, Excerpt (90 words max), Category, Tags
                    ============================================================ */}
                {activeSidebarTab === 'settings' && (
                  <>
                    {/* 1. Cover Image */}
                    <Box>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569', display: 'block', mb: 1, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        Cover Image
                      </Typography>
                      {formData.imageUrl ? (
                        <Box sx={{ position: 'relative', borderRadius: 2, overflow: 'hidden', border: '1px solid #e2e8f0', mb: 1.5 }}>
                          <img
                            src={formData.imageUrl}
                            alt={formData.imageAlt || 'Cover'}
                            style={{ width: '100%', height: 160, objectFit: 'cover', display: 'block' }}
                          />
                          <Box sx={{ display: 'flex', gap: 1, p: 1, bgcolor: '#ffffff', borderTop: '1px solid #f1f5f9' }}>
                            <Button
                              size="small"
                              component="label"
                              startIcon={<Upload size={14} />}
                              sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                            >
                              Change
                              <input type="file" accept="image/*" hidden onChange={handleCoverUpload} />
                            </Button>
                            <Button
                              size="small"
                              color="error"
                              startIcon={<Trash2 size={14} />}
                              onClick={() => {
                                setHasUnsavedChanges(true);
                                setFormData((prev) => ({ ...prev, imageUrl: '' }));
                              }}
                              sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                            >
                              Remove
                            </Button>
                          </Box>
                        </Box>
                      ) : (
                        <label className="blog-sidebar-cover-dropzone" style={{ marginBottom: '1rem' }}>
                          <input type="file" accept="image/*" hidden onChange={handleCoverUpload} />
                          <Upload size={24} style={{ color: '#94a3b8', margin: '0 auto 6px' }} />
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155' }}>
                            Upload Cover Image
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                            Recommended 1600 × 900 px
                          </Typography>
                        </label>
                      )}
                    </Box>

                    {/* 2. Image Alt Text */}
                    <TextField
                      label="Alt text of image"
                      size="small"
                      fullWidth
                      value={formData.imageAlt || ''}
                      onChange={(e) => {
                        setHasUnsavedChanges(true);
                        setFormData((prev) => ({ ...prev, imageAlt: e.target.value }));
                      }}
                      placeholder="Describe image for SEO and screen readers"
                      helperText="Helps image ranking on Google"
                    />

                    {/* 3. Excerpt (Description - 90 words max) */}
                    <Box>
                      <TextField
                        label="Excerpt"
                        size="small"
                        multiline
                        rows={3}
                        fullWidth
                        value={formData.shortDescription || ''}
                        onChange={(e) => {
                          setHasUnsavedChanges(true);
                          setFormData((prev) => ({ ...prev, shortDescription: e.target.value }));
                        }}
                        placeholder="Brief summary shown on blog cards and search results..."
                      />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 600,
                            color: excerptWordCount > 90 ? 'error.main' : 'text.secondary'
                          }}
                        >
                          {excerptWordCount} / 90 words
                        </Typography>
                        {excerptWordCount > 90 && (
                          <Typography variant="caption" color="error">
                            Exceeds 90 words limit
                          </Typography>
                        )}
                      </Box>
                    </Box>

                    {/* 4. Category selection dropdown */}
                    <Autocomplete
                      freeSolo
                      options={CATEGORY_OPTIONS}
                      value={formData.category || ''}
                      onChange={(_, newVal) => {
                        setHasUnsavedChanges(true);
                        setFormData((prev) => ({ ...prev, category: newVal || '' }));
                      }}
                      onInputChange={(_, newInputValue) => {
                        setHasUnsavedChanges(true);
                        setFormData((prev) => ({ ...prev, category: newInputValue || '' }));
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          label="Category"
                          placeholder="Select or enter category"
                        />
                      )}
                    />

                    {/* 5. Tags (multiple) */}
                    <Autocomplete
                      multiple
                      freeSolo
                      options={[
                        'Trekking',
                        'Himachal',
                        'Uttarakhand',
                        'Heritage',
                        'Wildlife',
                        'Camping',
                        'Monsoon',
                        'Winter',
                        'Summer'
                      ]}
                      value={Array.isArray(formData.tags) ? formData.tags : []}
                      onChange={(_, newTags) => {
                        setHasUnsavedChanges(true);
                        setFormData((prev) => ({ ...prev, tags: newTags }));
                      }}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            label={option}
                            size="small"
                            {...getTagProps({ index })}
                            key={option + index}
                            sx={{ fontWeight: 500 }}
                          />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          label="Tags (multiple)"
                          placeholder="Type & press enter"
                          helperText="Add keywords for search & filtering"
                        />
                      )}
                    />

                    {/* Author & Custom Slug */}
                    <TextField
                      label="Author"
                      size="small"
                      fullWidth
                      value={formData.author || ''}
                      onChange={(e) => {
                        setHasUnsavedChanges(true);
                        setFormData((prev) => ({ ...prev, author: e.target.value }));
                      }}
                    />

                    <TextField
                      label="URL Slug"
                      size="small"
                      fullWidth
                      value={formData.slug || ''}
                      onChange={(e) => {
                        setHasUnsavedChanges(true);
                        setFormData((prev) => ({ ...prev, slug: slugify(e.target.value) }));
                      }}
                      helperText="Used in public link: /blogs/your-slug"
                    />
                  </>
                )}

                {/* ============================================================
                    PANEL 2: SEO & URL SLUG
                    Google Search Preview, Slug (Permalink) Editor, Meta Title, Meta Description
                    ============================================================ */}
                {activeSidebarTab === 'seo' && (
                  <>
                    {/* Google Search Engine Preview Card */}
                    <Box sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                          Search Engine Preview
                        </Typography>
                        <Chip label="Google Preview" size="small" sx={{ fontSize: '0.65rem', height: 20, bgcolor: '#e0f2fe', color: '#0369a1', fontWeight: 600 }} />
                      </Box>

                      <Box sx={{ bgcolor: '#ffffff', p: 1.5, borderRadius: 1.5, border: '1px solid #e2e8f0' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                          <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: '#de5824', color: '#fff', fontSize: '0.6rem', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold' }}>
                            I
                          </Box>
                          <Typography variant="caption" sx={{ color: '#202124', fontSize: '0.72rem', wordBreak: 'break-all' }}>
                            invinciblengo.org › blogs › {formData.slug || 'your-blog-slug'}
                          </Typography>
                        </Box>
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#1a0dab',
                            fontWeight: 600,
                            fontSize: '0.95rem',
                            lineHeight: 1.3,
                            mb: 0.5,
                            cursor: 'pointer',
                            '&:hover': { textDecoration: 'underline' }
                          }}
                        >
                          {formData.metaTitle || formData.title || 'Your Blog Post Title'}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#4d5156', fontSize: '0.75rem', lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                          {formData.metaDescription || formData.shortDescription || 'Enter a meta description or excerpt to preview how this blog will look on Google search engine results...'}
                        </Typography>
                      </Box>
                    </Box>

                    {/* URL Slug Editor */}
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                          URL Slug (Permalink)
                        </Typography>
                        <Button
                          size="small"
                          onClick={() => {
                            if (formData.title) {
                              setHasUnsavedChanges(true);
                              setFormData((prev) => ({ ...prev, slug: slugify(prev.title) }));
                            }
                          }}
                          sx={{ textTransform: 'none', fontSize: '0.72rem', p: 0, minWidth: 'auto' }}
                        >
                          Reset from title
                        </Button>
                      </Box>
                      <TextField
                        size="small"
                        fullWidth
                        value={formData.slug || ''}
                        onChange={(e) => {
                          setHasUnsavedChanges(true);
                          setFormData((prev) => ({ ...prev, slug: slugify(e.target.value) }));
                        }}
                        placeholder="e.g. why-valley-of-flowers"
                        helperText={`Public URL: /blogs/${formData.slug || 'slug'}`}
                      />
                    </Box>

                    {/* Meta Title */}
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                          Meta Title (SEO Title)
                        </Typography>
                        <Typography variant="caption" sx={{ color: (formData.metaTitle?.length || 0) > 60 ? '#ef4444' : '#64748b' }}>
                          {formData.metaTitle?.length || 0} / 60
                        </Typography>
                      </Box>
                      <TextField
                        size="small"
                        fullWidth
                        value={formData.metaTitle || ''}
                        onChange={(e) => {
                          setHasUnsavedChanges(true);
                          setFormData((prev) => ({ ...prev, metaTitle: e.target.value }));
                        }}
                        placeholder={formData.title || 'Enter SEO title'}
                        helperText="Appears as the main clickable headline on search engines (optimal 50-60 characters)"
                      />
                    </Box>

                    {/* Meta Description */}
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                          Meta Description
                        </Typography>
                        <Typography variant="caption" sx={{ color: (formData.metaDescription?.length || 0) > 160 ? '#ef4444' : '#64748b' }}>
                          {formData.metaDescription?.length || 0} / 160
                        </Typography>
                      </Box>
                      <TextField
                        size="small"
                        fullWidth
                        multiline
                        rows={3}
                        value={formData.metaDescription || ''}
                        onChange={(e) => {
                          setHasUnsavedChanges(true);
                          setFormData((prev) => ({ ...prev, metaDescription: e.target.value }));
                        }}
                        placeholder="Summary shown beneath the title in search engine snippets..."
                        helperText="Provide a clear, engaging summary of the article (optimal 140-160 characters)"
                      />
                    </Box>
                  </>
                )}

                {/* ============================================================
                    PANEL 3: CONTENT & RELATIONSHIPS
                    Related posts, Related events, Featured post toggle
                    ============================================================ */}
                {activeSidebarTab === 'content' && (
                  <>
                    {/* 1. Toggle to feature the post on top */}
                    <Box sx={{ p: 1.5, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={Boolean(formData.isFeatured)}
                            onChange={(e) => {
                              setHasUnsavedChanges(true);
                              setFormData((prev) => ({ ...prev, isFeatured: e.target.checked }));
                            }}
                            color="primary"
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#0f172a' }}>
                              Feature post on top
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
                              Highlight this blog as a featured story at the top of the blog page
                            </Typography>
                          </Box>
                        }
                      />
                    </Box>

                    {/* 2. Select Related Posts options */}
                    <Box>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569', display: 'block', mb: 1, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        Related Posts
                      </Typography>
                      <Autocomplete
                        multiple
                        options={allBlogs.filter((b) => String(b.id) !== String(id))}
                        getOptionLabel={(option) => option.title || ''}
                        isOptionEqualToValue={(option, value) => option.id === value?.id || option.id === value}
                        value={allBlogs.filter((b) => (formData.relatedPostIds || []).includes(b.id))}
                        onChange={(_, selectedOptions) => {
                          setHasUnsavedChanges(true);
                          setFormData((prev) => ({
                            ...prev,
                            relatedPostIds: selectedOptions.map((o) => o.id)
                          }));
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            placeholder="Search & select blogs"
                            helperText="These posts appear in 'More Stories' under this blog"
                          />
                        )}
                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => (
                            <Chip
                              label={option.title}
                              size="small"
                              {...getTagProps({ index })}
                              key={option.id}
                            />
                          ))
                        }
                      />
                    </Box>

                    {/* 3. Select Related Events */}
                    <Box>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569', display: 'block', mb: 1, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        Related Treks & Events
                      </Typography>
                      <Autocomplete
                        multiple
                        options={allEvents}
                        getOptionLabel={(option) => option.name || option.title || ''}
                        isOptionEqualToValue={(option, value) => option.id === value?.id || option.id === value}
                        value={allEvents.filter((e) => (formData.relatedEventIds || []).includes(e.id))}
                        onChange={(_, selectedOptions) => {
                          setHasUnsavedChanges(true);
                          setFormData((prev) => ({
                            ...prev,
                            relatedEventIds: selectedOptions.map((o) => o.id)
                          }));
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            placeholder="Search & select events/treks"
                            helperText="Displayed in the sticky sidebar on the public blog page"
                          />
                        )}
                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => (
                            <Chip
                              label={option.name || option.title}
                              size="small"
                              {...getTagProps({ index })}
                              key={option.id}
                            />
                          ))
                        }
                      />
                    </Box>
                  </>
                )}
              </div>
            </div>
          )}
        </aside>
      </div>

      {/* ------------------------------------------------------------------
          4. MEDIA INSERTION POPUP MODAL (+ PLUS ICON POPUP)
          Image, Gallery, Video (embedded link or upload)
          ------------------------------------------------------------------ */}
      <Dialog
        open={mediaModalOpen}
        onClose={() => !mediaBusy && setMediaModalOpen(false)}
        maxWidth="sm"
        fullWidth
        slotProps={{
          paper: {
            sx: { borderRadius: 3, p: 1 }
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Insert Media
          </Typography>
          <IconButton size="small" onClick={() => setMediaModalOpen(false)} disabled={mediaBusy}>
            <X size={18} />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 1 }}>
          <Tabs
            value={mediaTab}
            onChange={(_, val) => setMediaTab(val)}
            variant="fullWidth"
            className="blog-media-dialog-tabs"
            sx={{
              mb: 2.5,
              '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: '0.9rem' }
            }}
          >
            <Tab icon={<ImageIcon size={18} />} iconPosition="start" label="Image" />
            <Tab icon={<LayoutGrid size={18} />} iconPosition="start" label="Gallery Grid" />
            <Tab icon={<Video size={18} />} iconPosition="start" label="Video" />
          </Tabs>

          {/* TAB 0: IMAGE */}
          {mediaTab === 0 && (
            <Box>
              <RadioGroup
                row
                value={imageMode}
                onChange={(e) => setImageMode(e.target.value)}
                sx={{ mb: 2 }}
              >
                <FormControlLabel value="upload" control={<Radio size="small" />} label="Upload File" />
                <FormControlLabel value="url" control={<Radio size="small" />} label="Image URL" />
              </RadioGroup>

              {imageMode === 'upload' ? (
                <label className="blog-media-dropzone" style={{ display: 'block' }}>
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={(e) => setImageFile(e.target.files?.[0] || null)}
                  />
                  <Upload size={32} style={{ color: '#94a3b8', margin: '0 auto 8px' }} />
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155' }}>
                    {imageFile ? imageFile.name : 'Click to select or drag image here'}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                    JPG, PNG, WebP up to 10MB
                  </Typography>
                </label>
              ) : (
                <TextField
                  label="Image URL"
                  fullWidth
                  size="small"
                  placeholder="https://example.com/photo.jpg"
                  value={imageUrlInput}
                  onChange={(e) => setImageUrlInput(e.target.value)}
                  sx={{ mb: 2 }}
                />
              )}

              <TextField
                label="Caption / Alt Text (Optional)"
                fullWidth
                size="small"
                placeholder="Brief description of the image"
                value={imageCaption}
                onChange={(e) => setImageCaption(e.target.value)}
                sx={{ mt: 2, mb: 2 }}
              />

              <Typography variant="caption" sx={{ fontWeight: 600, color: '#475569', display: 'block', mb: 0.5 }}>
                Alignment
              </Typography>
              <RadioGroup
                row
                value={imageAlign}
                onChange={(e) => setImageAlign(e.target.value)}
              >
                <FormControlLabel value="center" control={<Radio size="small" />} label="Center" />
                <FormControlLabel value="wide" control={<Radio size="small" />} label="Full Width" />
                <FormControlLabel value="left" control={<Radio size="small" />} label="Float Left" />
                <FormControlLabel value="right" control={<Radio size="small" />} label="Float Right" />
              </RadioGroup>
            </Box>
          )}

          {/* TAB 1: GALLERY GRID */}
          {mediaTab === 1 && (
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155', mb: 1 }}>
                Choose Bento Grid Size:
              </Typography>
              <RadioGroup
                row
                value={galleryCount}
                onChange={(e) => setGalleryCount(Number(e.target.value))}
                sx={{ mb: 2 }}
              >
                <FormControlLabel value={3} control={<Radio size="small" />} label="3 Images (Hero + 2)" />
                <FormControlLabel value={4} control={<Radio size="small" />} label="4 Images (2×2)" />
                <FormControlLabel value={6} control={<Radio size="small" />} label="6 Images (Bento)" />
              </RadioGroup>

              <label className="blog-media-dropzone" style={{ display: 'block' }}>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  hidden
                  onChange={(e) => {
                    const picked = Array.from(e.target.files || []).slice(0, galleryCount);
                    setGalleryFiles(picked);
                  }}
                />
                <LayoutGrid size={32} style={{ color: '#94a3b8', margin: '0 auto 8px' }} />
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155' }}>
                  {galleryFiles.length > 0
                    ? `${galleryFiles.length} / ${galleryCount} images selected`
                    : `Click to choose ${galleryCount} images for this grid`}
                </Typography>
                <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                  Uploads directly and renders a responsive mobile-friendly bento gallery
                </Typography>
              </label>
            </Box>
          )}

          {/* TAB 2: VIDEO */}
          {mediaTab === 2 && (
            <Box>
              <RadioGroup
                row
                value={videoMode}
                onChange={(e) => setVideoMode(e.target.value)}
                sx={{ mb: 2 }}
              >
                <FormControlLabel value="embed" control={<Radio size="small" />} label="Embed Link (YouTube / Vimeo)" />
                <FormControlLabel value="upload" control={<Radio size="small" />} label="Upload Video File" />
              </RadioGroup>

              {videoMode === 'embed' ? (
                <Box>
                  <TextField
                    label="Video URL (YouTube or Vimeo)"
                    fullWidth
                    size="small"
                    placeholder="https://www.youtube.com/watch?v=..."
                    value={videoUrlInput}
                    onChange={(e) => setVideoUrlInput(e.target.value)}
                    helperText="Supports youtube.com, youtu.be, and vimeo.com URLs"
                  />
                  {videoUrlInput && (
                    <Box sx={{ mt: 2, borderRadius: 2, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                      <iframe
                        src={getEmbedUrl(videoUrlInput)}
                        title="Video Preview"
                        style={{ width: '100%', height: 200, border: 'none' }}
                      />
                    </Box>
                  )}
                </Box>
              ) : (
                <label className="blog-media-dropzone" style={{ display: 'block' }}>
                  <input
                    type="file"
                    accept="video/mp4,video/webm"
                    hidden
                    onChange={(e) => setVideoFile(e.target.files?.[0] || null)}
                  />
                  <Video size={32} style={{ color: '#94a3b8', margin: '0 auto 8px' }} />
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155' }}>
                    {videoFile ? videoFile.name : 'Click to select MP4 / WebM video file'}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                    Directly uploaded and embedded with native player controls
                  </Typography>
                </label>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setMediaModalOpen(false)} disabled={mediaBusy} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleInsertMedia}
            disabled={mediaBusy}
            startIcon={mediaBusy ? <CircularProgress size={16} color="inherit" /> : <Plus size={16} />}
            sx={{
              bgcolor: '#ff5500',
              textTransform: 'none',
              fontWeight: 700,
              '&:hover': { bgcolor: '#e04b00' }
            }}
          >
            {mediaBusy ? 'Uploading & Inserting…' : 'Insert into Blog'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ------------------------------------------------------------------
          5. BLOG PREVIEW MODAL
          ------------------------------------------------------------------ */}
      {previewOpen && (
        <BlogPreviewModal
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
          blog={{
            ...formData,
            id: isEditing ? id : undefined
          }}
          isUnsavedPreview={!isEditing}
          onSaveReorderedContent={(reorderedHtml) => {
            const editor = quillRef.current?.getEditor();
            if (editor) {
              editor.clipboard.dangerouslyPasteHTML(0, reorderedHtml, 'silent');
            }
            setFormData((prev) => ({ ...prev, content: reorderedHtml }));
            setHasUnsavedChanges(true);
          }}
        />
      )}
    </div>
  );
};

export default BlogEditor;
